import { useTranslation } from 'next-i18next'
import styled from 'styled-components'

import { breakpoints, colors, fontWeights } from '@/styles'
import { TextButton } from '@/components/button'

interface ArticleProps {
  wide?: boolean
  name: string
  logo: string
  link: string | string[]
}

const Logo = styled.div<Pick<ArticleProps, 'logo' | 'wide'>>`
  height: 100px;
  ${props =>
    props.logo &&
    `
    background-image: url(${props.logo});
    background-size: 100% auto;
    background-repeat: no-repeat;
    background-position: 50%;
  `}
  width: ${props => (props.wide ? 200 : 100)}px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    height: 75px;
    width: ${props => (props.wide ? 110 : 85)}px;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    height: 50px;
    width: ${props => (props.wide ? 90 : 70)}px;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${colors.white};
`

const Link = styled(TextButton)`
  text-decoration: none;
  font-size: 18px;
  font-weight: ${fontWeights.medium};
  line-height: 24px;
  text-align: center;
  letter-spacing: -0.2px;

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    font-size: 14px;
    line-height: 19px;
  }
`

const LinkContainer = styled.div`
  > a:not(:last-of-type) {
    position: relative;
    margin-right: 5px;
    padding-right: 5px;
    &:after {
      position: absolute;
      content: '';
      height: 20px;
      width: 1px;
      background: ${colors.link};
      opacity: 0.7;
      right: -1px;
      bottom: 0;
    }
  }
`

const Article = ({ link: linkOrLinks, logo, wide, name }: ArticleProps) => {
  const links = Array.isArray(linkOrLinks) ? linkOrLinks : [linkOrLinks]
  const hasOneLink = links.length <= 1
  const { t } = useTranslation('about')
  return (
    <Container>
      <Logo
        role="img"
        aria-labelledby="background-image-label"
        logo={logo}
        wide={wide}
      />
      <span id="background-image-label" style={{ display: 'none' }}>
        {name}
      </span>
      <LinkContainer>
        {links.map((link, key) => (
          <Link
            as="a"
            target="_blank"
            rel="noopener noreferrer"
            key={`readmore-link-${key}`}
            href={link}
          >
            {hasOneLink
              ? t('about:Read more')
              : t('about:Link', {
                  defaultValue: 'Link {{number}}',
                  number: key + 1
                })}
          </Link>
        ))}
      </LinkContainer>
    </Container>
  )
}

export default Article
